import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Award, Trophy, Target, TrendingUp } from 'lucide-react';
import { BadgesGrid, useBadges, BADGES } from '../components/Badges';
import PageHeader from '../components/PageHeader';
import Footer from '../components/Footer';
import { BackToTopButton } from '../components/NavigationHelpers';
import { authFetch } from '../lib/authFetch';
import useTheme4A from '../components/useTheme4A';

function BadgesPage() {
  useTheme4A();
  const navigate = useNavigate();
  const { earnedBadges, loading, checkAndAwardBadges } = useBadges();
  const [stats, setStats] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');

  const fetchStats = useCallback(async () => {
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/badges/stats`);
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    checkAndAwardBadges();
  }, [fetchStats, checkAndAwardBadges]);

  // Filter badges by category
  const categories = {
    all: 'All Badges',
    meetings: 'Meetings',
    sobriety: 'Sobriety',
    engagement: 'Engagement'
  };

  const filterBadges = () => {
    const allBadges = Object.values(BADGES);
    if (activeCategory === 'all') return allBadges;
    if (activeCategory === 'meetings') {
      return allBadges.filter(b => b.requirement.type === 'meetings' || b.requirement.type === 'streak');
    }
    if (activeCategory === 'sobriety') {
      return allBadges.filter(b => b.requirement.type === 'sobriety_days');
    }
    if (activeCategory === 'engagement') {
      return allBadges.filter(b => b.requirement.type === 'journals' || b.requirement.type === 'group_messages');
    }
    return allBadges;
  };

  const totalBadges = Object.keys(BADGES).length;
  const earnedCount = earnedBadges.length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 pb-20">
      <PageHeader 
        title="Achievements"
      />

      <main className="pt-20 pb-6 px-4">
        <div className="max-w-md mx-auto">
          {/* Stats Overview */}
          <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-6 mb-6 text-center">
            <Trophy className="w-12 h-12 text-amber-400 mx-auto mb-3" />
            <h2 className="text-3xl font-bold text-white mb-1">{earnedCount} / {totalBadges}</h2>
            <p className="text-white/60 text-sm">Badges Earned</p>
            
            {/* Progress bar */}
            <div className="mt-4 h-2 bg-white/20 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-amber-400 to-yellow-400 transition-all duration-500"
                style={{ width: `${(earnedCount / totalBadges) * 100}%` }}
              />
            </div>
          </div>

          {/* Quick Stats */}
          {stats && (
            <div className="grid grid-cols-2 gap-3 mb-6">
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-lg p-4 text-center">
                <Target className="w-6 h-6 text-green-400 mx-auto mb-2" />
                <p className="text-xl font-bold text-white">{stats.meetings}</p>
                <p className="text-white/60 text-xs">Meetings</p>
              </div>
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-lg p-4 text-center">
                <TrendingUp className="w-6 h-6 text-blue-400 mx-auto mb-2" />
                <p className="text-xl font-bold text-white">{stats.sobriety_days}</p>
                <p className="text-white/60 text-xs">Days Clean</p>
              </div>
            </div>
          )}

          {/* Category Tabs */}
          <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
            {Object.entries(categories).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setActiveCategory(key)}
                className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  activeCategory === key
                    ? 'bg-white text-blue-700'
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
                data-testid={`category-${key}`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Badges Grid */}
          <div className="bg-white rounded-xl p-4">
            {loading ? (
              <div className="text-center py-8">
                <p className="text-zinc-500">Loading badges...</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3">
                {filterBadges().map((badge) => {
                  const earned = earnedBadges.find(e => e.badge_id === badge.id);
                  const IconComponent = badge.icon;
                  
                  return (
                    <div 
                      key={badge.id}
                      className={`relative p-4 rounded-xl border transition-all ${
                        earned 
                          ? 'bg-white border-zinc-200 shadow-sm' 
                          : 'bg-zinc-50 border-zinc-100 opacity-60'
                      }`}
                      data-testid={`badge-${badge.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-14 h-14 rounded-full ${badge.color} flex items-center justify-center ${!earned && 'grayscale'}`}>
                          <IconComponent className="w-7 h-7 text-white" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-zinc-900 text-sm">{badge.name}</h4>
                          <p className="text-zinc-500 text-xs">{badge.description}</p>
                          {earned && (
                            <p className="text-xs text-green-600 mt-1">
                              Earned {new Date(earned.earned_at).toLocaleDateString()}
                            </p>
                          )}
                          {!earned && (
                            <p className="text-xs text-zinc-400 mt-1">
                              Requires: {badge.requirement.count} {badge.requirement.type.replace('_', ' ')}
                            </p>
                          )}
                        </div>
                        {!earned && (
                          <span className="text-xs bg-zinc-200 text-zinc-600 px-2 py-1 rounded">
                            Locked
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </main>
      
      <BackToTopButton />
      <Footer />
    </div>
  );
}

export default BadgesPage;
